import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getMaintenanceStatus } from '../services/api';
import './MaintenanceCheck.css';

const MaintenanceCheck = ({ children }) => {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const location = useLocation();

  const timerRef = useRef(null);

  useEffect(() => {
    checkMaintenance();

    const interval = setInterval(() => {
      if (isMaintenance) {
        checkMaintenance();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(timerRef.current);
    };
  }, [location, isMaintenance]);

  const checkMaintenance = async () => {
    if (location.pathname.includes('/admin/')) {
      setLoading(false);
      return;
    }

    try {
      const response = await getMaintenanceStatus();

      if (response.success && response.isMaintenance) {
        setIsMaintenance(true);
        setMaintenanceInfo(response.maintenance);
        startTimer(response.maintenance.endTime);
      } else {
        if (isMaintenance) {
          setTimeout(() => window.location.reload(), 2000);
        }
        setIsMaintenance(false);
      }
    } catch (error) {
      if (error?.type === 'MAINTENANCE') {
        setIsMaintenance(true);
        setMaintenanceInfo(error.data.maintenance);
        startTimer(error.data.maintenance.endTime);
      } else {
        console.error('Maintenance check error:', error);
        setIsMaintenance(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED TIMER (single interval only)
  const startTimer = (endTime) => {
    clearInterval(timerRef.current);

    const update = () => {
      const now = new Date();
      const end = new Date(endTime);
      const diff = end - now;

      if (diff > 0) {
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeLeft(
          `${h.toString().padStart(2, '0')}:${m
            .toString()
            .padStart(2, '0')}:${s.toString().padStart(2, '0')}`
        );
      } else {
        setTimeLeft('00:00:00');
        clearInterval(timerRef.current);
        setTimeout(() => window.location.reload(), 3000);
      }
    };

    update();
    timerRef.current = setInterval(update, 1000);
  };

  if (loading) {
    return (
      <div className="maintenance-loading-container">
<div class="boxes">
    <div class="box">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
    </div>
    <div class="box">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
    </div>
    <div class="box">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
    </div>
    <div class="box">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
    </div>
</div>
      </div>
    );
  }

  if (isMaintenance && !location.pathname.includes('/admin/')) {
    return <MaintenancePage info={maintenanceInfo} timeLeft={timeLeft} />;
  }

  return children;
};

const MaintenancePage = ({ info, timeLeft }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);

      if (info?.startTime && info?.endTime) {
        const start = new Date(info.startTime).getTime();
        const end = new Date(info.endTime).getTime();
        const curr = now.getTime();

        if (curr >= start && curr <= end) {
          const percent = ((curr - start) / (end - start)) * 100;
          setProgress(Math.min(Math.max(percent, 0), 100));
        } else if (curr > end) {
          setProgress(100);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [info]);

  return (
    <div className="maintenance-page-container">
      <div className="maintenance-content-card">
        <h1>🚧 Scheduled Maintenance</h1>

        <div className="countdown-value">{timeLeft}</div>

        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* ✅ Label fixed */}
        <div className="current-time-display">
          <span>Current Time (IST)</span>
          <strong>
            {currentTime.toLocaleTimeString('en-IN', {
              timeZone: 'Asia/Kolkata',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </strong>
        </div>

        <p>
          Maintenance Type: <b>{info?.title}</b>
        </p>
      </div>
    </div>
  );
};

export default MaintenanceCheck;
